import * as React from 'react';
// @ts-expect-error
import type {RouterHistory} from 'react-router-dom';

import classNames from 'classnames';

import {ButtonCard} from '../card/button-card';
import {colors} from '../colors/colors';
import type {ColorName} from '../colors/colors';
import {GlossaryBookmarkIcon, MinimizeIcon, TickCircleIcon} from '../icon';
import {Body} from '../typography/body';

import {
    getCta,
    getDescription,
    onCardClick,
    getThumbnail,
    getStyleVariables,
    getIconColorFromVariant,
} from './helpers';

import styles from './cue-card.module.css';

export type CueCardVariant = 'default' | 'ai' | 'feature';
export type CueCardCtaAction =
    | 'external-link'
    | 'routed-link'
    | 'modal'
    | 'video'
    | 'intercom-article';

const VARIANT_TO_BORDER_VARIANT: Partial<Record<CueCardVariant, CueCardVariant>> = {
    default: 'default',
    ai: 'ai',
    feature: 'feature',
};

export type CueCardCta = {
    text?: string;
    actionType?: CueCardCtaAction;
    onClick?: () => any;
    url?: string;
    newTab?: boolean;
    youTubeVideoId?: string;
};

export type GradientConfig = {
    colors: ColorName[];
    angle?: number; // Defaults to 90
};

type Props = {
    title: string;
    description?: string | React.ComponentType<any>;
    cta?: CueCardCta;
    thumbnailIllustration?: string | React.ReactNode;
    thumbnailPosition?: 'full' | 'centered' | 'no-resize';
    thumbnailBackgroundColor?: ColorName;
    thumbnailBackgroundColorHover?: ColorName;
    thumbnailBackgroundGradientHover?: GradientConfig;
    withBookmarkIcon?: boolean;
    onMinimize?: () => void;
    history: RouterHistory;
    isComplete?: boolean; // If true, the card will have a green background and a green check in the corner,
    icon?: React.ComponentType<any>; // Displays above the title and under the thumbnail,
    variant?: CueCardVariant; // 'ai' variant uses purple colors for icon, CTA, and hover border,
    fitHeight?: boolean;
};

export function CueCard(props: Props) {
    const {thumbnailPosition = 'centered', variant = 'default', fitHeight = true} = props;
    const Icon = props.icon;

    const containerStyle = classNames({
        [styles['thumbnail-container--centered']]: thumbnailPosition === 'centered',
        [styles['thumbnail-container--full']]: thumbnailPosition === 'full',
        [styles['thumbnail-container--no-resize']]: thumbnailPosition === 'no-resize',
    });

    const hasPlayButton = props.cta && props.cta.onClick && props.cta.actionType === 'video';
    const iconColor = getIconColorFromVariant(variant);

    const styleVariables = getStyleVariables(
        props.thumbnailBackgroundColor,
        props.thumbnailBackgroundColorHover,
        props.thumbnailBackgroundGradientHover
    );

    return (
        <ButtonCard
            fitHeight={fitHeight}
            isFullWidth={true}
            // @ts-expect-error
            onClick={props.cta ? () => onCardClick(props.cta, props.history) : undefined}
            borderVariant={VARIANT_TO_BORDER_VARIANT[variant]}
        >
            <div className={styles['content-container']}>
                {(props.thumbnailBackgroundColor || props.thumbnailIllustration) && (
                    <div style={styleVariables} className={containerStyle}>
                        {getThumbnail(
                            props.thumbnailIllustration,
                            hasPlayButton,
                            props.cta && props.cta.onClick
                        )}
                    </div>
                )}
                {props.isComplete && (
                    <div className={styles.complete}>
                        <TickCircleIcon size={16} color={colors.green} alignCenter={true} />
                    </div>
                )}
                {props.withBookmarkIcon && (
                    <span className={styles['bookmark-icon-container']}>
                        <GlossaryBookmarkIcon size={26} wrapWithDiv={false} />
                    </span>
                )}
                {props.onMinimize && (
                    <div
                        onClick={(e) => {
                            e.stopPropagation();
                            if (props.onMinimize) {
                                props.onMinimize();
                            }
                        }}
                        className={styles['minimize-button']}
                    >
                        <MinimizeIcon size={12} wrapWithDiv={false} />
                    </div>
                )}
                <div className={styles['text-container']}>
                    {Icon ? <Icon wrapWithDiv={false} size={32} color={iconColor} /> : undefined}
                    <Body color='dark' isBold={true}>
                        {props.title}
                    </Body>
                    {props.description ? (
                        <div className='flex-grow'>{getDescription(props.description)}</div>
                    ) : undefined}
                    {getCta(props.cta, variant)}
                </div>
            </div>
        </ButtonCard>
    );
}
